

































































import ContentCard from '@/components/ContentCard.vue';
import { computed, defineComponent, Ref, ref, watch } from '@vue/composition-api';
import { TemplateModel, useGetTemplatesAndDevicesCountQuery } from '@/graphql/types';
import useNavigateToTemplate from '@/composables/useNavigateToTemplate';
import Table from '@/components/Table.vue';
import { TableField } from '@/types/TableField';
import { useResult } from '@vue/apollo-composable';

export default defineComponent({
  components: { ContentCard, Table },
  setup(_, { root }) {
    const defaultSort = ref({
      sortBy: 'name',
      sortDesc: false
    });
    const perPage = 40;
    const currentPage = ref(1);
    const nameFilter = ref(null as string | null);
    const attributeFilter = ref(null as string | null);
    const templates = ref<any[]>([]);
    const filtered = ref([] as TemplateModel[]);
    const sortedTemplateItems = ref<any[]>([]);

    const { result, loading, error, refetch } = useGetTemplatesAndDevicesCountQuery({
      fetchPolicy: 'cache-and-network'
    });

    watch(
      () => useResult(result).value,
      newValue => {
        if (newValue) {
          const templateItems: any[] = [];
          newValue.forEach(t =>
            templateItems.push({
              id: t.id,
              name: t.name ?? '',
              description: t.description ?? '',
              attributes: t.attributes,
              deviceCount: t.deviceCount,
              attributesCount: attributeCount(t),
              devicesCount: deviceCount(t)
            })
          );
          templates.value = [...templateItems];
          setSortedTemplates();
        }
      },
      { immediate: true }
    );

    watch(
      () => currentPage.value,
      newValue => {
        setSortedTemplates();
      },
      { immediate: true }
    );

    root.$on('refetchTemplates', (newTemplate: any) => {
      if (newTemplate) {
        newTemplate.attributesCount = attributeCount(newTemplate);
        newTemplate.devicesCount = deviceCount(newTemplate);
        templates.value.push(newTemplate);
        filtered.value = [newTemplate, ...filtered.value];
        setSortedTemplates(filtered.value);
      } else {
        refetch();
      }
    });

    function useFilteringTemplates(
      templates: Ref<TemplateModel[]>,
      nameFilter: Ref<string | null>,
      attributeFilter: Ref<string | null>
    ) {
      watch([templates, nameFilter, attributeFilter], () => {
        currentPage.value = 1;
        filter(templates.value, nameFilter.value, attributeFilter.value);
        setSortedTemplates(filtered.value);
      });

      async function filter(templates: TemplateModel[], nameFilter: string | null, attributeFilter: string | null) {
        filtered.value = templates.filter(element => {
          var isValid = true;

          if (nameFilter && isValid) {
            isValid = element.name.toLowerCase().indexOf(nameFilter.toLowerCase()) >= 0;
          }

          if (attributeFilter && isValid) {
            isValid = element.attributes.some(
              x =>
                (x.name && x.name.toLowerCase().indexOf(attributeFilter.toLowerCase()) >= 0) ||
                (x.unit && x.unit.toLowerCase().indexOf(attributeFilter.toLowerCase()) >= 0) ||
                x.sensorMapping?.toLowerCase().indexOf(attributeFilter.toLowerCase()) >= 0
            );
          }

          return isValid;
        });
      }
      return filtered;
    }

    function attributeCount(template: any) {
      return `${template.attributes.length} ${template.attributes.length > 1 ? ' attributter' : ' attribut'}`;
    }

    function deviceCount(template: any) {
      return `${template.deviceCount ?? 0} ${template.deviceCount > 1 ? ' enheder' : ' enhed'}`;
    }

    function navigateToTemplate(router: any, template: any) {
      useNavigateToTemplate(router, template.id);
    }

    function setSortedTemplates(items: any = null) {
      const asc = (a: any, b: any) => a[defaultSort.value.sortBy]?.localeCompare(b[defaultSort.value.sortBy], 'da');
      const desc = (a: any, b: any) => b[defaultSort.value.sortBy]?.localeCompare(a[defaultSort.value.sortBy], 'da');
      templates.value = templates.value.sort(defaultSort.value.sortDesc ? desc : asc);
      sortedTemplateItems.value = (items ?? templates.value).slice(
        (currentPage.value - 1) * perPage,
        (currentPage.value - 1) * perPage + perPage
      );
    }

    function onSortChanged($event: any) {
      defaultSort.value = {
        sortBy: $event.sortBy,
        sortDesc: $event.sortDesc
      };
      setSortedTemplates();
    }

    function isMissingValue(templateAttributes: any) {
      return templateAttributes.some((x: any) => !x.sc1009Id || !x.sc1187Id);
    }

    return {
      loading,
      error,
      templates: useFilteringTemplates(templates, nameFilter, attributeFilter),
      sortedTemplateItems: computed(() => sortedTemplateItems.value),
      nameFilter,
      attributeFilter,
      useNavigateToTemplate,
      navigateToTemplate,
      attributeCount,
      deviceCount,
      perPage,
      currentPage,
      onSortChanged,
      isMissingValue,
      fields: computed<TableField[]>(() => [
        {
          key: 'name',
          sortable: true,
          label: root.$tc('templates.fields.name'),
          width: '35%'
        },
        {
          key: 'attributesCount',
          formatter: (value, key, item) => attributeCount(item),
          sortable: true,
          sortByFormatted: true,
          label: root.$tc('templates.fields.attributes'),
          width: '35%'
        },
        {
          key: 'devicesCount',
          formatter: (value, key, item) => deviceCount(item),
          sortable: true,
          sortByFormatted: true,
          label: root.$tc('templates.fields.devices'),
          width: '30%'
        }
      ])
    };
  }
});
